import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";

async function dispose(scanner) {
  if (!scanner) return;
  const state = scanner.getState?.();
  const active = scanner.isScanning || (state !== undefined && (state === Html5QrcodeScannerState?.SCANNING || state === Html5QrcodeScannerState?.PAUSED));
  try { if (active) await scanner.stop(); } catch (error) { console.debug("Scanner stop:", error); }
  try { scanner.clear(); } catch (error) { console.debug("Scanner clear:", error); }
}

export default function useQrScanner({ onResult, onStatus }) {
  const [scanning, setScanning] = useState(false);
  const [closing, setClosing] = useState(false);
  const qrContainerRef = useRef(null);
  const qrFileInputRef = useRef(null);
  const scannerRef = useRef(null);
  const cleanupRef = useRef(null);
  const cleanupPromiseRef = useRef(Promise.resolve());
  const callbacksRef = useRef({ onResult, onStatus });
  callbacksRef.current = { onResult, onStatus };
  const generationRef = useRef(0);

  const reset = useCallback(() => {
    generationRef.current += 1;
    const cleanup = cleanupRef.current?.();
    if (cleanup) { setClosing(true); cleanup.finally(() => setClosing(false)); }
    setScanning(false);
    if (qrFileInputRef.current) qrFileInputRef.current.value = "";
    return cleanupPromiseRef.current;
  }, []);

  useEffect(() => {
    if (!scanning || !qrContainerRef.current) return undefined;
    let cancelled = false;
    const containerId = qrContainerRef.current.id;
    let scanner;
    const start = async () => {
      try {
        await cleanupPromiseRef.current;
        if (cancelled) return;
        scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        const devices = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!devices.length) throw new Error("No cameras found");
        const device = devices.find((item) => item.label.toLowerCase().includes("back")) || devices[0];
        await scanner.start(device.id, { fps: 10, qrbox: (width, height) => {
          const size = Math.floor(Math.min(250, width * .8, height * .8));
          return { width: size, height: size };
        } }, (text) => {
          if (cancelled) return;
          callbacksRef.current.onResult(text);
          setScanning(false);
        }, () => {});
        // start() resolves before the library's playing handler creates its canvas.
        // Keep the surface visible until that handler runs, including on cancel.
        const video = qrContainerRef.current?.querySelector?.("video");
        if (video) await video.play();
      } catch (error) {
        if (!cancelled) {
          callbacksRef.current.onStatus("Unable to access camera");
          setScanning(false);
        }
      }
    };
    const startup = start();
    let cleanup;
    const stop = () => {
      if (cleanup) return cleanup;
      cancelled = true;
      if (scannerRef.current === scanner) scannerRef.current = null;
      // Let start finish before touching the library's media/state machine.
      cleanup = startup.then(() => dispose(scanner));
      cleanupPromiseRef.current = cleanup;
      return cleanup;
    };
    cleanupRef.current = stop;
    return () => { void stop(); if (cleanupRef.current === stop) cleanupRef.current = null; };
  }, [scanning]);

  useEffect(() => () => { generationRef.current += 1; }, []);

  const handleScanClick = () => {
    if (closing) return;
    if (scanning) void reset();
    else setScanning(true);
    callbacksRef.current.onStatus(scanning ? "" : "Opening camera for QR...");
  };

  const handleScanFile = async (file) => {
    callbacksRef.current.onStatus("Scanning image...");
    const generation = generationRef.current;
    const scanner = new Html5Qrcode("qr-reader-hidden");
    try {
      const text = await scanner.scanFile(file, false);
      if (generation === generationRef.current) callbacksRef.current.onResult(text);
    } catch (error) {
      if (generation === generationRef.current) callbacksRef.current.onStatus("Failed to scan QR");
    } finally {
      await dispose(scanner);
    }
  };

  return { scanning, closing, qrContainerRef, qrFileInputRef, reset, handleScanClick, handleScanFile };
}
